/**
 * Free Access Configuration
 *
 * Default limits and feature-list builder for free-tier users
 * when Free Access Mode is enabled via the admin panel.
 *
 * The actual limit values are stored in the platform_settings table
 * (keys: "free_access_max_stores", "free_access_max_products", etc.)
 * and fetched dynamically by FreeAccessContext.
 *
 * The constants below serve as fallback defaults when the DB
 * settings have not yet been configured.
 */

export interface FreeAccessLimits {
  max_stores: number;
  max_products: number;
  ai_optimizations_monthly: number;
  ai_chat_messages_monthly: number;
  max_bulk_operations_monthly: number;
}

/**
 * Default limits applied when Free Access Mode is active but no
 * custom limits have been saved in platform_settings yet.
 */
export const FREE_ACCESS_LIMITS_DEFAULTS: FreeAccessLimits = {
  max_stores: 3,
  max_products: 5000,
  ai_optimizations_monthly: -1, // unlimited
  ai_chat_messages_monthly: -1, // unlimited
  max_bulk_operations_monthly: -1, // unlimited
};

/** Format a single numeric limit as a human-readable string. */
function formatLimitValue(value: number, unit: string): string {
  if (value === -1) return `Unlimited ${unit}`;
  return `${value.toLocaleString()} ${unit}`;
}

/**
 * Build a feature list from the given limits, suitable for plan cards.
 */
export function buildFreeAccessFeatures(limits: FreeAccessLimits): string[] {
  return [
    `Up to ${limits.max_stores} store${limits.max_stores !== 1 ? "s" : ""}`,
    `Up to ${limits.max_products.toLocaleString()} products`,
    formatLimitValue(limits.ai_optimizations_monthly, "AI optimizations"),
    formatLimitValue(limits.ai_chat_messages_monthly, "AI chat messages"),
    formatLimitValue(limits.max_bulk_operations_monthly, "bulk operations"),
    "SEO scoring & insights",
    "Analytics dashboard",
    "Community support",
  ];
}

/** Database keys used to persist free access limits in platform_settings. */
export const FREE_ACCESS_SETTINGS_KEYS = {
  max_stores: "free_access_max_stores",
  max_products: "free_access_max_products",
  ai_optimizations_monthly: "free_access_ai_optimizations_monthly",
  ai_chat_messages_monthly: "free_access_ai_chat_messages_monthly",
  max_bulk_operations_monthly: "free_access_max_bulk_operations_monthly",
} as const;
